import { CheckCircle2, Eye, EyeOff, Send, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PageError, PageLoading } from '../../../components';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { BotaoCopiar } from '../components/copiar';
import { mensagemErro } from '../credenciais/use-credenciais';
import type { ApiWebhook, ResultadoTeste } from './webhook-service';
import { useWebhook, useWebhookMutations } from './use-webhook';

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'short',
  });

type Confirmacao = 'gerarSegredo' | 'remover';

// Aba Webhook: URL, ativo, segredo da assinatura e envio de teste da ops atual.
export function Webhook({ verDocumentacao }: { verDocumentacao: () => void }) {
  const { data: webhook, isLoading, error } = useWebhook();
  const { salvar, gerarSegredo, remover, testar } = useWebhookMutations();
  const [url, setUrl] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [resultado, setResultado] = useState<ResultadoTeste | null>(null);

  useEffect(() => {
    setUrl(webhook?.url ?? '');
    setAtivo(webhook?.ativo ?? true);
  }, [webhook]);

  if (isLoading) return <PageLoading loading message="Carregando webhook..." />;
  if (error) return <PageError error={mensagemErro(error)} />;

  const alterado = !webhook || url.trim() !== webhook.url || ativo !== webhook.ativo;

  const executar = async (acao: () => Promise<unknown>) => {
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };

  const onSalvar = () => {
    if (!url.trim().startsWith('https://')) {
      setErro('A URL precisa começar com https://');
      return;
    }
    setResultado(null);
    executar(() => salvar.mutateAsync({ url: url.trim(), ativo }));
  };

  const onTestar = () => {
    setResultado(null);
    executar(async () => setResultado(await testar.mutateAsync()));
  };

  const onConfirmar = () => {
    if (confirmacao === 'gerarSegredo') executar(() => gerarSegredo.mutateAsync());
    if (confirmacao === 'remover') {
      setResultado(null);
      executar(() => remover.mutateAsync());
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-[hsl(215,16%,47%)]">
        A cada 2 minutos, das 06:00 às 17:30, a Planalto envia para a sua URL as antecipações
        novas do dia, assinadas com o segredo abaixo.{' '}
        <button
          type="button"
          onClick={verDocumentacao}
          className="font-medium text-[hsl(210,80%,40%)] hover:underline"
        >
          Ver o que a URL precisa fazer
        </button>
      </p>

      <div className="space-y-3 rounded-md border border-[hsl(214,32%,91%)] p-4">
        <div className="space-y-2">
          <Label htmlFor="webhook-url">URL do webhook</Label>
          <Input
            id="webhook-url"
            type="url"
            value={url}
            maxLength={500}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://seu-sistema.com.br/webhooks/planalto"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
            className="h-4 w-4"
          />
          Ativo (desmarque para pausar os envios sem perder o cadastro)
        </label>
        <div className="flex gap-2">
          <Button onClick={onSalvar} disabled={!url.trim() || !alterado || salvar.isPending}>
            {salvar.isPending ? 'Salvando...' : webhook ? 'Salvar alterações' : 'Cadastrar webhook'}
          </Button>
        </div>
      </div>

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      {webhook && (
        <Cadastrado
          webhook={webhook}
          testando={testar.isPending}
          resultado={resultado}
          onTestar={onTestar}
          onGerarSegredo={() => setConfirmacao('gerarSegredo')}
          onRemover={() => setConfirmacao('remover')}
          verDocumentacao={verDocumentacao}
        />
      )}

      <ConfirmDialog
        open={!!confirmacao}
        onOpenChange={(open) => !open && setConfirmacao(null)}
        onConfirm={onConfirmar}
        title={confirmacao === 'remover' ? 'Remover o webhook?' : 'Gerar novo segredo?'}
        description={
          confirmacao === 'remover'
            ? 'Os envios param na hora. Para voltar a receber, cadastre a URL de novo (um segredo novo será gerado).'
            : 'Os próximos envios passam a ser assinados com o segredo novo. Atualize o seu sistema antes, se ele confere a assinatura.'
        }
        confirmText={confirmacao === 'remover' ? 'Remover' : 'Gerar novo segredo'}
        cancelText="Cancelar"
      />
    </div>
  );
}

function Cadastrado({
  webhook,
  testando,
  resultado,
  onTestar,
  onGerarSegredo,
  onRemover,
  verDocumentacao,
}: {
  webhook: ApiWebhook;
  testando: boolean;
  resultado: ResultadoTeste | null;
  onTestar: () => void;
  onGerarSegredo: () => void;
  onRemover: () => void;
  verDocumentacao: () => void;
}) {
  const [mostrarSegredo, setMostrarSegredo] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm text-[hsl(215,16%,47%)]">
        {webhook.ativo ? <Badge variant="secondary">Ativo</Badge> : <Badge variant="outline">Pausado</Badge>}
        <span>Atualizado em {dataHora(webhook.atualizadoEm)}</span>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-[hsl(210,50%,10%)]">Segredo da assinatura</span>
        <div className="flex flex-wrap items-center gap-2">
          <code
            data-testid="webhook-segredo"
            className="break-all rounded border border-[hsl(214,32%,91%)] bg-[hsl(210,40%,98%)] px-2 py-1 text-xs"
          >
            {mostrarSegredo ? webhook.segredo : `whsec_${'•'.repeat(12)}${webhook.segredo.slice(-4)}`}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => setMostrarSegredo((v) => !v)}
          >
            {mostrarSegredo ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {mostrarSegredo ? 'Ocultar' : 'Mostrar'}
          </Button>
          <BotaoCopiar texto={webhook.segredo} rotulo="Copiar segredo" />
          <Button variant="outline" size="sm" onClick={onGerarSegredo}>
            Gerar novo segredo
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Button variant="outline" className="gap-2" onClick={onTestar} disabled={testando}>
          <Send className="h-4 w-4" />
          {testando ? 'Enviando...' : 'Enviar teste'}
        </Button>
        {resultado && <ResultadoDoTeste resultado={resultado} verDocumentacao={verDocumentacao} />}
      </div>

      <div className="border-t border-[hsl(214,32%,91%)] pt-4">
        <Button variant="destructive" size="sm" onClick={onRemover}>
          Remover webhook
        </Button>
      </div>
    </div>
  );
}

function ResultadoDoTeste({
  resultado,
  verDocumentacao,
}: {
  resultado: ResultadoTeste;
  verDocumentacao: () => void;
}) {
  if (resultado.ok) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        Recebido: HTTP {resultado.status} em {resultado.duracaoMs} ms.
      </p>
    );
  }
  return (
    <div role="status" className="space-y-1 text-sm text-red-600">
      <p className="flex items-center gap-2">
        <XCircle className="h-4 w-4" />
        Falhou: {resultado.status ? `HTTP ${resultado.status}` : resultado.erro}.
      </p>
      <p className="text-[hsl(215,16%,47%)]">
        A URL precisa responder com status 2xx em até 10 segundos, sem redirecionar.{' '}
        <button
          type="button"
          onClick={verDocumentacao}
          className="font-medium text-[hsl(210,80%,40%)] hover:underline"
        >
          Ver documentação
        </button>
      </p>
    </div>
  );
}
